<template>
  <div class="flex flex-row gap-2">
    <div ref="root" class="flex flex-col w-full" @blur="isKeyboardOpen = false">
      <button
        v-if="replyToMessage"
        class="flex flex-row items-center gap-2 justify-between h-10 pl-4 py-2 my-2"
        type="button"
        @click.stop="emit('goToUpstream', replyToMessage.id)"
      >
        <div class="text-xs text-basics-tertiary-60">
          <span class="sr-only"
            >{{ $t("channel.chat.scrollIntoMessage") }}
          </span>

          <div>
            <p
              class="text-brand-primary text-sm font-medium font-sans leading-tight line-clamp-1 break-good"
            >
              {{
                $t("channel.chat.replyTo", {
                  username: replyToMessage.author.username,
                })
              }}
            </p>
            <p
              class="text-white/80 text-xs font-medium font-sans leading-tight text-left truncate max-w-[180px]"
            >
              {{ replyToMessageContent }}
            </p>
          </div>
        </div>

        <Button
          class="w-10 h-10 text-xs bg-transparent border-transparent p-1 min-h-0"
          type="button"
          variant="icon"
          @click.stop="clearReply"
        >
          <Icon icon="mdi:close" class="w-5 h-5 text-lg text-basics-primary" />
        </Button>
      </button>

      <div class="flex flex-row gap-2 items-center w-full !mb-1">
        <form
          id="chat-message-form"
          class="sticky min-h-[3.125rem] w-full bottom-0 z-10 bg-brand-surface rounded-3xl pl-4 pr-0.5 py-1 focus-within:border-brand-primary border-2 border-border-brand gap-2.5 transition-all flex flex-col justify-center"
          @click.stop
          @submit.prevent="sendMessage"
        >
          <div class="flex flex-row items-center gap-1">
            <textarea
              id="chat-message-input"
              ref="inputRef"
              v-model="input"
              type="text"
              name="message"
              :aria-label="$t('channel.chat.typeHere')"
              :placeholder="$t('channel.chat.typeHere')"
              :maxlength="300"
              :rows="1"
              required
              class="w-full placeholder:text-text-tertiary text-md p-0 text-white bg-transparent border-none focus:outline-none resize-none border user-invalid:border-basics-accent-red overflow-x-auto max-h-20 font-medium font-sans whitespace-nowrap hide-scrollbar"
              @focus="onTextInputFocus"
              @keydown="onEnterPress"
            ></textarea>
            <!-- :size="isMobile ? 'md' : 'sm'" -->
            <ButtonV2
              v-if="isKeyboardOpen"
              variant="square"
              type="submit"
              size="md"
              :disabled="input.length === 0"
              aria-label="Send message"
            >
              <Icon
                icon="material-symbols-light:arrow-upward-alt-rounded"
                class="w-5 h-5 text-lg text-basics-primary"
                aria-hidden
              />
            </ButtonV2>
          </div>
        </form>

        <slot></slot>
      </div>
    </div>

    <slot name="right"></slot>
  </div>
</template>

<script setup lang="ts">
import { watch, computed, useTemplateRef } from "vue";
import Button from "../ui/button/button.vue";
import { Icon } from "@iconify/vue";
import { useTextareaAutosize } from "@vueuse/core";
import { useObjectTranslation } from "@/stores/translation.store";
import ButtonV2 from "../ui/button/button-v2.vue";
import { useI18n } from "vue-i18n";
import type { IChatMessage } from "@/stores/chat/model/chat-model";

const MAX_FIELD_LINES = 5;

const { t } = useI18n();

const message = defineModel<string>("message", {
  required: true,
});

const isKeyboardOpen = defineModel<boolean>("isKeyboardOpen", {
  required: false,
});

const emit = defineEmits<{
  submit: [];
  clearReply: [];
  goToUpstream: [messageId: string];
  setExpanded: [expanded: boolean];
}>();

const props = defineProps<{
  replyToMessage: IChatMessage | null;
  isMobile?: boolean | null;
}>();

const rootRef = useTemplateRef("root");
defineExpose({
  root: rootRef,
});

const { textarea: inputRef, input } = useTextareaAutosize({
  input: message,
});

watch(
  () => props.replyToMessage,
  () => {
    message.value = "";
    inputRef.value?.focus();
  },
);

watch(message, () => {
  const lines = message.value.split("\n").length;
  if (lines > MAX_FIELD_LINES) {
    inputRef.value?.setCustomValidity(
      t("channel.chat.tooManyLines", { lines }),
    );
    inputRef.value?.reportValidity();
  } else {
    inputRef.value?.setCustomValidity("");
  }
});

function sendMessage() {
  inputRef.value?.focus(); // Set focus back to the textarea
  emit("submit");
}

const onEnterPress = (e: KeyboardEvent) => {
  if (e.key === "Enter" && !e.shiftKey) {
    // Prevent form auto-submit on Enter
    e.preventDefault();

    // Validate if the input is valid
    const isValid = (e.target as HTMLTextAreaElement).checkValidity();

    // If valid, attempt to send the message
    if (isValid) {
      sendMessage();
      clearReply();
    }
  } else if (e.key === "Escape") {
    clearReply();
  }
};

function clearReply() {
  emit("clearReply");
}

function onTextInputFocus() {
  setTimeout(() => {
    isKeyboardOpen.value = true;
    emit("setExpanded", true);
  }, 100);
}

const { text: replyToMessageContent } = useObjectTranslation(
  computed(() => {
    if (!props.replyToMessage) return null;
    return {
      authorUserId: props.replyToMessage.author.id,
      originalText: props.replyToMessage.content,
      objectAccessors: {
        objectId: props.replyToMessage.id,
        objectProperty: "content",
      },
      translatedText: props.replyToMessage.messageTranslation,
    };
  }),
);
</script>

<style lang="css" scoped>
@supports (-webkit-overflow-scrolling: touch) {
  textarea {
    font-size: 16px;
  }
}
</style>
